import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listReturnUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { normalizeDate } from '../lib/dates';
import { useAuth, can } from '../lib/auth';

interface DieForm {
  dieNo: string; designNo: string; dieDate: string;
  dieUps: string; dieVendor: string;
  awLength: string; awWidth: string; awHeight: string; artWorkDesignedBy: string;
  sheetCutSizeLength: string; sheetCutSizeWidth: string;
}

const empty: DieForm = {
  dieNo: '', designNo: '', dieDate: '',
  dieUps: '', dieVendor: '',
  awLength: '', awWidth: '', awHeight: '', artWorkDesignedBy: '',
  sheetCutSizeLength: '', sheetCutSizeWidth: '',
};

export function DieDesignFormPage() {
  // Master records are readable office-wide, writable by Super Admin / VPPL.
  const { user } = useAuth();
  const canEditMasters = can(user, 'EDIT_MASTERS');
  const { id } = useParams();
  const isEdit = Boolean(id);
  const decodedId = id ? decodeURIComponent(id) : id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<DieForm>(empty);
  const [error, setError] = useState<string | null>(null);

  const existing = useQuery({
    enabled: isEdit,
    queryKey: ['die-design', decodedId],
    queryFn: () => api<DieForm & { id: string }>(`/api/die-designs/${encodeURIComponent(decodedId!)}`),
  });

  useEffect(() => {
    if (existing.data) setForm({ ...empty, ...existing.data });
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () =>
      api<{ id: string }>(isEdit ? `/api/die-designs/${encodeURIComponent(decodedId!)}` : '/api/die-designs', {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(form),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['die-designs'] });
      navigate('/die-designs');
    },
    onError: (e) => setError((e as Error).message),
  });

  function update<K extends keyof DieForm>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.dieNo.trim() && !form.designNo.trim()) {
      setError('Die No or Design No is required.');
      return;
    }
    save.mutate();
  }

  if (isEdit && existing.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-brand">
          {isEdit ? `Edit Die Design — ${decodedId}` : 'New Die Design'}
        </h1>
        <Link to={listReturnUrl('/die-designs')} className="text-sm text-slate-600 hover:underline">← Back</Link>
      </div>

      {!canEditMasters && (
        <div className="mb-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          View only — die designs are maintained by Super Admin and VPPL.
        </div>
      )}

      <form className="space-y-4" onSubmit={onSubmit}>
        {/* One disabled fieldset: a field added later cannot stay editable. */}
        <fieldset disabled={!canEditMasters} className="space-y-4 disabled:opacity-100">
        <Section title="Die">
          <F label="Die No" value={form.dieNo} onChange={(v) => update('dieNo', v)} />
          <F label="Die Received Date" type="date" value={form.dieDate} onChange={(v) => update('dieDate', v)} />
          <F label="Die Ups" value={form.dieUps} onChange={(v) => update('dieUps', v)} />
          <F label="Die Vendor" value={form.dieVendor} onChange={(v) => update('dieVendor', v)} />
        </Section>

        <Section title="Design & Artwork">
          <F label="Design No" value={form.designNo} onChange={(v) => update('designNo', v)} />
          <F label="Artwork Length" value={form.awLength} onChange={(v) => update('awLength', v)} />
          <F label="Artwork Width" value={form.awWidth} onChange={(v) => update('awWidth', v)} />
          <F label="Artwork Height" value={form.awHeight} onChange={(v) => update('awHeight', v)} />
          <F label="Designed By" value={form.artWorkDesignedBy} onChange={(v) => update('artWorkDesignedBy', v)} />
          <F label="Sheet Cut Size L" value={form.sheetCutSizeLength} onChange={(v) => update('sheetCutSizeLength', v)} />
          <F label="Sheet Cut Size W" value={form.sheetCutSizeWidth} onChange={(v) => update('sheetCutSizeWidth', v)} />
        </Section>
        </fieldset>

        {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <Link to={listReturnUrl('/die-designs')} className="btn-ghost">
            {canEditMasters ? 'Cancel' : 'Back'}
          </Link>
          {canEditMasters && (
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-header">{title}</div>
      <div className="card-body grid gap-4 md:grid-cols-3">{children}</div>
    </div>
  );
}

function F({ label, value, onChange, type = 'text', placeholder }:
  { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  const displayValue = type === 'date' ? normalizeDate(value) : value;
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" type={type} value={displayValue} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
